import { AlertTriangle, Blocks, ChevronDown, Loader2, Plus, Search, Shield, Users, Wrench, X, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateExtensionModal } from "@/components/extensions/CreateExtensionModal";
import { resolveExtensionIcon } from "@/components/extensions/IconColorPickers";
import { PageHeader } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { extensionService } from "@/lib/services";
import type { ExtensionSummary, ExtensionType } from "@/types";

// ---------------------------------------------------------------------------
// Status filter options
// ---------------------------------------------------------------------------

type StatusFilter = "all" | "active" | "inactive" | "needs_setup";

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "needs_setup", label: "Needs Setup" },
];

// ---------------------------------------------------------------------------
// Section config
// ---------------------------------------------------------------------------

const sectionConfig: { type: ExtensionType; title: string; description: string }[] = [
  { type: "integration", title: "Integrations", description: "Third-party service connections" },
  { type: "platform", title: "Platform", description: "Built-in platform capabilities" },
  { type: "custom", title: "Custom", description: "Organization-created extensions" },
];

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: Readonly<{ status: string }>) {
  if (status === "active") return null;

  const config: Record<string, { label: string; className: string }> = {
    needs_setup: {
      label: "Needs Setup",
      className: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    },
    error: {
      label: "Error",
      className: "bg-red-500/10 text-red-400 border-red-500/20",
    },
    inactive: {
      label: "Inactive",
      className: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    },
  };

  const cfg = config[status] || config.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded ${cfg!.className}`}>
      {cfg!.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

function ToggleSwitch({
  checked,
  disabled,
  onChange,
}: Readonly<{ checked: boolean; disabled?: boolean; onChange: () => void }>) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <span className="sr-only">Toggle extension</span>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only peer" />
      <div
        className={`w-11 h-6 bg-surface-tertiary peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full peer-checked:rtl:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:inset-s-[2px] after:bg-white after:border-border-hover after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 ${disabled ? "opacity-50" : ""}`}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function Extensions() {
  const { user } = useAuth();

  if (!user?.isPlatformAdmin) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="h-16 w-16 rounded-2xl bg-red-500/20 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-content-primary">Access Denied</h2>
          <p className="mt-2 text-content-tertiary max-w-sm">
            You don't have permission to access extensions management. Contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return <ExtensionsContent />;
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

function ExtensionsContent() {
  const navigate = useNavigate();

  const [extensions, setExtensions] = useState<ExtensionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [toggling, setToggling] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchExtensions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await extensionService.list();
      setExtensions(response.data);
    } catch (err) {
      console.error("Failed to fetch extensions:", err);
      setError("Failed to load extensions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  const handleToggle = async (uuid: string, currentEnabled: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setToggling(uuid);
      const response = await extensionService.toggle(uuid, !currentEnabled);
      setExtensions((prev) =>
        prev.map((ext) =>
          ext.uuid === uuid ? { ...ext, isEnabled: response.data.isEnabled, status: response.data.status } : ext,
        ),
      );
    } catch (err) {
      console.error("Failed to toggle extension:", err);
      setError("Failed to toggle extension. Please try again.");
    } finally {
      setToggling(null);
    }
  };

  // Filter extensions
  const filtered = extensions.filter((ext) => {
    const matchesSearch =
      ext.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (ext.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && ext.isEnabled && ext.status === "active") ||
      (statusFilter === "inactive" && !ext.isEnabled) ||
      (statusFilter === "needs_setup" && ext.status === "needs_setup");

    return matchesSearch && matchesStatus;
  });

  // Group by extension type
  const grouped = sectionConfig
    .map((section) => ({
      ...section,
      extensions: filtered.filter((ext) => ext.extensionType === section.type),
    }))
    .filter((section) => section.extensions.length > 0);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <PageHeader icon={Blocks} title="Extensions" subtitle="Manage your organization's tools and capabilities" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        icon={Blocks}
        title="Extensions"
        subtitle="Manage your organization's tools and capabilities"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-brand-gradient text-white font-semibold px-6 py-3 rounded-lg hover:opacity-90 transition"
          >
            <Plus className="w-5 h-5" />
            Add New
          </button>
        }
      >
        {/* Error alert */}
        {error && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-red-400">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Search + filter row */}
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-content-tertiary" />
            <input
              type="text"
              placeholder="Search extensions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-surface-primary border border-border-primary rounded-lg pl-10 pr-4 py-2.5 text-content-primary placeholder-content-tertiary focus:outline-hidden focus:border-brand-primary transition"
            />
          </div>

          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="appearance-none pl-4 pr-10 py-2.5 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:border-brand-primary"
            >
              {statusFilterOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary pointer-events-none" />
          </div>
        </div>
      </PageHeader>

      <div className="flex-1 overflow-auto p-6 space-y-8">
        {grouped.length === 0 ? (
          <div className="text-center py-12">
            <Blocks className="w-16 h-16 text-content-quaternary mx-auto mb-4" />
            <p className="text-content-tertiary text-lg mb-2">No extensions found</p>
            <p className="text-content-quaternary text-sm">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "No extensions have been configured yet"}
            </p>
          </div>
        ) : (
          grouped.map((section) => (
            <div key={section.type}>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-content-primary">{section.title}</h2>
                <p className="text-sm text-content-tertiary">{section.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.extensions.map((ext) => {
                  const IconComponent = resolveExtensionIcon(ext.icon);
                  const isToggling = toggling === ext.uuid;
                  const iconBg = ext.color ? "" : "bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20";
                  const iconColor = ext.color ? "" : "text-brand-primary";
                  const teamLabel =
                    ext.teamAccessCount === 0
                      ? "All teams"
                      : `${ext.teamAccessCount} ${ext.teamAccessCount === 1 ? "team" : "teams"}`;
                  const handleToggleClick = () =>
                    handleToggle(ext.uuid, ext.isEnabled, { stopPropagation: () => {} } as React.MouseEvent);

                  return (
                    <div
                      key={ext.uuid}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/extensions/${ext.uuid}`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/extensions/${ext.uuid}`);
                        }
                      }}
                      className="bg-surface-secondary/50 border border-border-secondary rounded-xl p-5 hover:border-border-hover transition cursor-pointer group"
                    >
                      {/* Header row: icon + name + status */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}
                            style={ext.color ? { backgroundColor: `${ext.color}20` } : undefined}
                          >
                            <IconComponent
                              className={`w-5 h-5 ${iconColor}`}
                              style={ext.color ? { color: ext.color } : undefined}
                            />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-content-primary truncate">{ext.name}</h3>
                          </div>
                        </div>
                        <StatusBadge status={ext.status} />
                      </div>

                      {/* Description */}
                      {ext.description && (
                        <p className="text-sm text-content-tertiary mb-3 line-clamp-2">{ext.description}</p>
                      )}

                      {/* Stats row */}
                      {(ext.toolCount > 0 || ext.skillCount > 0) && (
                        <div className="flex items-center gap-3 mb-3 text-xs text-content-quaternary">
                          {ext.toolCount > 0 && (
                            <span className="flex items-center gap-1">
                              <Wrench className="w-3.5 h-3.5" />
                              {ext.toolCount} {ext.toolCount === 1 ? "tool" : "tools"}
                            </span>
                          )}
                          {ext.skillCount > 0 && (
                            <span className="flex items-center gap-1">
                              <Zap className="w-3.5 h-3.5" />
                              {ext.skillCount} {ext.skillCount === 1 ? "skill" : "skills"}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Footer: toggle + access */}
                      <div className="flex items-center justify-between pt-3 border-t border-border-secondary">
                        <span className="flex items-center gap-1 text-xs text-content-quaternary">
                          <Users className="w-3.5 h-3.5" />
                          {teamLabel}
                        </span>

                        <div
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                          aria-hidden="true"
                        >
                          <ToggleSwitch checked={ext.isEnabled} disabled={isToggling} onChange={handleToggleClick} />
                          {isToggling && <Loader2 className="w-4 h-4 text-content-tertiary animate-spin inline ml-2" />}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <CreateExtensionModal
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchExtensions();
          }}
        />
      )}
    </div>
  );
}
